/**
 * Module code normalization for consistent RBAC matching
 * Maps various module code formats to canonical uppercase codes
 */

const MODULE_ALIASES = {
  // Inventory module aliases
  'inventory': 'INVENTORY',
  'INVENTORY': 'INVENTORY',
  'inv': 'INVENTORY',
  'INV': 'INVENTORY',
  'inventory_management': 'INVENTORY',
  'INVENTORY_MANAGEMENT': 'INVENTORY',
  
  // Purchase/Expense Capture module aliases
  'vendor_engine': 'PURCHASE',
  'VENDOR_ENGINE': 'PURCHASE',
  'expense_capture': 'PURCHASE',
  'EXPENSE_CAPTURE': 'PURCHASE',
  'purchase': 'PURCHASE',
  'PURCHASE': 'PURCHASE',
  
  // Kitchen ops aliases
  'kitchen_ops': 'KITCHEN_OPS',
  'KITCHEN_OPS': 'KITCHEN_OPS',
  'kitchen': 'KITCHEN_OPS',
  'KITCHEN': 'KITCHEN_OPS',
};

/**
 * Normalize a module code to canonical uppercase format
 * @param {string|null|undefined} code - Module code string (can be any case/alias)
 * @returns {string|undefined} Normalized uppercase module code
 */
export const normalizeModule = (code) => {
  if (!code) return undefined;
  
  const c = code.trim();
  const cUp = c.toUpperCase();
  
  // Check exact match first
  if (c in MODULE_ALIASES) {
    return MODULE_ALIASES[c];
  }
  
  // Check uppercase match
  if (cUp in MODULE_ALIASES) {
    return MODULE_ALIASES[cUp];
  }
  
  // Return uppercase version if no alias found
  return cUp;
};

/**
 * Normalize a list of module codes
 * @param {string[]|null|undefined} codes - Array of module code strings
 * @returns {string[]} Array of normalized uppercase module codes
 */
export const normalizeModuleList = (codes) => {
  if (!codes) return [];
  return codes.map(normalizeModule).filter((c) => c !== undefined);
};

/**
 * Check if user has access to a specific module
 * @param {string[]|undefined} allowedApps - User's allowed apps/modules
 * @param {string} requiredModule - Module code to check
 * @returns {boolean} true if user has access
 */
export const hasModuleAccess = (allowedApps, requiredModule) => {
  const normalizedRequired = normalizeModule(requiredModule);
  const normalizedApps = normalizeModuleList(allowedApps);
  
  // Check for wildcard
  if (normalizedApps.includes('*')) {
    return true;
  }
  
  return normalizedRequired ? normalizedApps.includes(normalizedRequired) : false;
};

export default normalizeModule;
